import React, { useState, useMemo } from 'react'

import { LinkOutlined, EditOutlined, DeleteOutlined, QuestionCircleOutlined } from '@ant-design/icons'
import { Tag, Space, Button, Tooltip, App } from 'antd'
import { MultipleValue, SearchValue } from '@shared/table/Rules/component'
import TableIndex from '@shared/table/TableIndex'
import type { TableService, SupportRule, TableAction } from '@shared/table/TableIndex'
import { useTranslation } from 'react-i18next'
import { Link, useNavigate } from 'react-router-dom'

interface InventoryMonitorService extends TableService {
  delete: (id: string) => Promise<{ success: boolean; message?: string }>;
  batchDelete: (ids: string[]) => Promise<{ success: boolean; message?: string }>;
}

interface InventoryTypeService {
  fetchList: (params: Record<string, unknown>) => Promise<{ data: Array<{ id: string; name: string; code: string }> }>;
  [key: string]: unknown;
}

interface MerchantOption {
  label: string;
  value: string;
}

interface InventoryMonitorRow {
  id: string;
  product?: { id: string; name: string; sku: string };
  merchant_name?: string;
  inventory_type?: string;
  mechanism?: string;
  tracking_stocks?: number;
  threshold_stocks?: number;
  stock_health_ratio?: number;
  created_at?: string;
  [key: string]: unknown;
}

interface InventoryMonitorsListProps {
  service: InventoryMonitorService; // New
  inventoryTypeService?: InventoryTypeService; // New
  role?: 'admin' | 'operator';
  merchantOptions?: MerchantOption[];
  hideMerchantUI?: boolean; // Operator single-merchant 模式使用
}

const InventoryMonitorsList: React.FC<InventoryMonitorsListProps> = ({
  service, // New
  inventoryTypeService, // New
  role = 'admin',
  merchantOptions = [],
  hideMerchantUI = false // Operator single-merchant 模式使用
}) => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { modal, message } = App.useApp()
  const [lastModified, setLastModified] = useState(0)

  // 刪除監控
  const handleDelete = async (id: string) => {
    modal.confirm({
      title: t('shared.inventory_monitors.list.confirmations.delete_title'),
      content: t('shared.inventory_monitors.list.confirmations.delete_content'),
      okText: t('common.button.confirm'),
      cancelText: t('common.button.cancel'),
      okButtonProps: { danger: true },
      onOk: async () => {
        try {
          let response
          if (service) {
            response = await service.delete(id)
          } else {
            response = await (service as InventoryMonitorService).delete(id)
          }

          if (response.success) {
            message.success(t('common.message.delete_success'))
            setLastModified(Date.now())
          } else {
            message.error(response.message || t('common.message.delete_failed'))
          }
        } catch (error) {
          message.error(t('common.message.delete_failed'))
        }
      },
    })
  }

  // 健康狀態渲染
  const renderHealthStatus = (record: InventoryMonitorRow) => {
    const ratio = record.stock_health_ratio ?? 0
    const percentage = (ratio * 100).toFixed(0)

    let color, icon

    if (ratio < 0.5) {
      color = 'red'
      icon = '🔴'
    } else if (ratio < 1) {
      color = 'orange'
      icon = '🟠'
    } else {
      color = 'green'
      icon = '🟢'
    }

    return (
      <Space size={4}>
        <span>{icon}</span>
        <span style={{
          fontFamily: 'monospace',
          fontSize: '14px',
          fontWeight: 600,
          color: color === 'red' ? '#ff4d4f' : color === 'orange' ? '#fa8c16' : '#52c41a'
        }}>
          {percentage}%
        </span>
      </Space>
    )
  }

  // 機制顯示
  const renderMechanism = (mechanism: string | undefined) => {
    const mechanismMap: Record<string, { text: string; color: string }> = {
      quantity: { text: t('shared.inventory_monitors.form.mechanisms.quantity'), color: 'blue' },
      turnover_days: { text: t('shared.inventory_monitors.form.mechanisms.turnover_days'), color: 'cyan' },
      stalled_days: { text: t('shared.inventory_monitors.form.mechanisms.stalled_days'), color: 'purple' },
    }
    const { text, color } = (mechanism && mechanismMap[mechanism]) || { text: mechanism, color: 'default' }
    return <Tag color={color}>{text}</Tag>
  }

  const columns = useMemo(() => [
    {
      nameKey: 'shared.inventory_monitors.list.columns.product',
      persist: true,
      defaultVisible: true,
      sortable: true,
      sortFieldName: 'products.name',
      width: '250px',
      wrap: true,
      cell: (record: InventoryMonitorRow) => {
        const { product } = record
        if (!product) return '-'
        return (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
            <div style={{ wordBreak: 'break-word' }}>
              <Link to={`/products/${product.id}`}>
                {product.name}
              </Link>
              <div style={{ fontSize: '12px', color: '#999' }}>{product.sku}</div>
            </div>
            <a
              href={`/${role}/products/${product.id}`}
              target="_blank"
              onClick={(e) => e.stopPropagation()}
              style={{ flexShrink: 0 }}
              rel="noreferrer"
            >
              <LinkOutlined style={{ fontSize: '12px', color: '#999' }} />
            </a>
          </div>
        )
      },
    },
    ...(role === 'operator' && !hideMerchantUI ? [{
      nameKey: 'shared.inventory_monitors.list.columns.merchant_name',
      persist: true,
      sortable: false,
      width: '150px',
      wrap: true,
      selector: ({ merchant_name }: InventoryMonitorRow) => merchant_name || '-',
    }] : []),
    {
      nameKey: 'shared.inventory_monitors.list.columns.inventory_type',
      persist: true,
      defaultVisible: true,
      sortable: false,
      width: '120px',
      wrap: true,
      selector: (record: InventoryMonitorRow) => record.inventory_type || '-',
    },
    {
      name: (
        <Space size={4}>
          {t('shared.inventory_monitors.list.columns.mechanism')}
          <Tooltip title={<span style={{ whiteSpace: 'pre-line' }}>{t('shared.inventory_monitors.list.column_tooltips.mechanism')}</span>}>
            <QuestionCircleOutlined style={{ color: '#999', fontSize: 12 }} />
          </Tooltip>
        </Space>
      ),
      persist: true,
      defaultVisible: true,
      sortable: false,
      width: '150px',
      wrap: true,
      cell: (record: InventoryMonitorRow) => renderMechanism(record.mechanism),
    },
    {
      name: (
        <Space size={4}>
          {t('shared.inventory_monitors.list.columns.current_stock')}
          <Tooltip title={t('shared.inventory_monitors.list.column_tooltips.current_stock')}>
            <QuestionCircleOutlined style={{ color: '#999', fontSize: 12 }} />
          </Tooltip>
        </Space>
      ),
      defaultVisible: true,
      sortable: true,
      sortFieldName: 'inventory_monitors.tracking_stocks',
      width: '120px',
      selector: (record: InventoryMonitorRow) => record.tracking_stocks || 0,
    },
    {
      name: (
        <Space size={4}>
          {t('shared.inventory_monitors.list.columns.threshold')}
          <Tooltip title={t('shared.inventory_monitors.list.column_tooltips.threshold')}>
            <QuestionCircleOutlined style={{ color: '#999', fontSize: 12 }} />
          </Tooltip>
        </Space>
      ),
      defaultVisible: true,
      sortable: true,
      sortFieldName: 'inventory_monitors.threshold_stocks',
      width: '120px',
      selector: (record: InventoryMonitorRow) => record.threshold_stocks || 0,
    },
    {
      name: (
        <Space size={4}>
          {t('shared.inventory_monitors.list.columns.health_status')}
          <Tooltip title={<span style={{ whiteSpace: 'pre-line' }}>{t('shared.inventory_monitors.list.column_tooltips.health_status')}</span>}>
            <QuestionCircleOutlined style={{ color: '#999', fontSize: 12 }} />
          </Tooltip>
        </Space>
      ),
      defaultVisible: true,
      sortable: true,
      sortFieldName: 'inventory_monitors.health_ratio',
      width: '120px',
      wrap: true,
      cell: (record: InventoryMonitorRow) => renderHealthStatus(record),
    },
    {
      nameKey: 'common.created_at',
      defaultVisible: true,
      sortable: true,
      sortFieldName: 'inventory_monitors.created_at',
      width: '150px',
      selector: ({ created_at }: InventoryMonitorRow) => created_at,
    },
    {
      nameKey: 'shared.inventory_monitors.list.columns.actions',
      persist: true,
      defaultVisible: true,
      sortable: false,
      width: '100px',
      cell: ({ id }: InventoryMonitorRow) => (
        <Space size={0}>
          <Tooltip title={t('common.button.edit')}>
            <Button
              type="text"
              size="small"
              icon={<EditOutlined />}
              onClick={(e) => {
                e.stopPropagation()
                navigate(`/inventory_monitors/${id}/edit`)
              }}
            />
          </Tooltip>
          <Tooltip title={t('common.button.delete')}>
            <Button
              type="text"
              size="small"
              danger
              icon={<DeleteOutlined />}
              onClick={(e) => {
                e.stopPropagation()
                handleDelete(id)
              }}
            />
          </Tooltip>
        </Space>
      ),
    }
  ], [role, t, navigate, handleDelete, hideMerchantUI])

  const supportRules = useMemo(() => {
    const rules: SupportRule[] = []

    // Operator 才有 Merchant 篩選（single-merchant 模式隱藏）
    if (role === 'operator' && !hideMerchantUI) {
      rules.push({
        label: t('shared.inventory_monitors.list.filters.merchant'),
        component: MultipleValue,
        inputName: 'merchant_id',
        defaultPinned: true,
        createable: false,
        defaultOptions: merchantOptions,
        tooltip: {
          title: t('shared.inventory_monitors.list.filter_tooltips.merchant.title'),
          content: t('shared.inventory_monitors.list.filter_tooltips.merchant.content'),
        },
      })
    }

    rules.push(
      {
        label: t('shared.inventory_monitors.list.filters.sku'),
        component: SearchValue,
        inputName: 'search',
        defaultPinned: true,
        tooltip: {
          title: t('shared.inventory_monitors.list.filter_tooltips.sku.title'),
          content: t('shared.inventory_monitors.list.filter_tooltips.sku.content'),
        },
      },
      {
        label: t('shared.inventory_monitors.list.filters.inventory_type'),
        component: MultipleValue,
        inputName: 'inventory_type_id',
        createable: false,
        lazyLoad: {
          action: inventoryTypeService ? (params: Record<string, unknown>) => inventoryTypeService.fetchList(params) : undefined,
          transform: (data: { data: Array<{ id: string; name: string; code: string }> }) => data.data.map((type) => ({
            label: `${type.name} (${type.code})`,
            value: type.id,
          })),
        },
        tooltip: {
          title: t('shared.inventory_monitors.list.filter_tooltips.inventory_type.title'),
          content: t('shared.inventory_monitors.list.filter_tooltips.inventory_type.content'),
        },
      },
      {
        label: t('shared.inventory_monitors.list.filters.mechanism'),
        component: MultipleValue,
        inputName: 'mechanism',
        createable: false,
        defaultOptions: [
          {
            label: t('shared.inventory_monitors.form.mechanisms.quantity'),
            value: 'quantity',
          },
          {
            label: t('shared.inventory_monitors.form.mechanisms.turnover_days'),
            value: 'turnover_days',
          },
          {
            label: t('shared.inventory_monitors.form.mechanisms.stalled_days'),
            value: 'stalled_days',
          },
        ],
        tooltip: {
          title: t('shared.inventory_monitors.list.filter_tooltips.mechanism.title'),
          content: t('shared.inventory_monitors.list.filter_tooltips.mechanism.content'),
        },
      },
      {
        label: t('shared.inventory_monitors.list.columns.health_status'),
        component: MultipleValue,
        inputName: 'health_status',
        createable: false,
        defaultOptions: [
          {
            label: t('shared.inventory_monitors.list.health_status.healthy'),
            value: 'healthy',
          },
          {
            label: t('shared.inventory_monitors.list.health_status.warning'),
            value: 'warning',
          },
          {
            label: t('shared.inventory_monitors.list.health_status.critical'),
            value: 'critical',
          },
        ],
        tooltip: {
          title: t('shared.inventory_monitors.list.filter_tooltips.health_status.title'),
          content: t('shared.inventory_monitors.list.filter_tooltips.health_status.content'),
        },
      }
    )

    return rules
  }, [role, t, merchantOptions, inventoryTypeService, hideMerchantUI])

  const actions = useMemo<TableAction[]>(() => {
    return [
      {
        name: 'shared.inventory_monitors.list.actions.batch_delete',
        danger: true,
        do: async (selected: any) => {
          modal.confirm({
            title: t('shared.inventory_monitors.list.confirmations.delete_title'),
            content: t('shared.inventory_monitors.list.confirmations.batch_delete', { count: selected.length }),
            okText: t('common.button.confirm'),
            cancelText: t('common.button.cancel'),
            okButtonProps: { danger: true },
            onOk: async () => {
              try {
                const response = await service.batchDelete(selected)
                if (response.success) {
                  message.success(t('common.message.delete_success'))
                  setLastModified(Date.now())
                } else {
                  message.error(response.message || t('common.message.delete_failed'))
                }
              } catch (error) {
                message.error(t('common.message.delete_failed'))
              }
            },
          })
        }
      } as TableAction
    ]
  }, [t, service, modal, message])

  return (
    <TableIndex
      columns={columns}
      service={service}
      supportRules={supportRules}
      actions={actions}
      lastModified={lastModified}
      setLastModified={setLastModified}
      filterHelpExample={t('shared.inventory_monitors.list.filter_help_example') as string}
      filterDocsUrl={`/docs/${role}/inventory_monitors/list#features`}
      tableId={undefined as unknown as string}
    />
  )
}

export default InventoryMonitorsList
